import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { io } from "socket.io-client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const ConnectionPage = () => {
  const { toast } = useToast();
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [whatsappName, setWhatsappName] = useState<string>("");

  useEffect(() => {
    const socket = io(import.meta.env.VITE_API_URL || 'http://localhost:3000');

    socket.on('qr', (qr: string) => {
      console.log('QR Code received');
      setQrCode(qr);
      setIsInitializing(true);
    });

    socket.on('ready', () => {
      console.log('WhatsApp Connected');
      setIsConnected(true);
      setIsInitializing(false);
      setQrCode(null);
      toast({
        title: "WhatsApp Conectado",
        description: "Sua conta do WhatsApp foi conectada com sucesso!",
      });
    });

    socket.on('disconnected', () => {
      console.log('WhatsApp Disconnected');
      setIsConnected(false);
      setIsInitializing(false);
      setQrCode(null);
      toast({
        title: "WhatsApp Desconectado",
        description: "A conexão com o WhatsApp foi encerrada.",
        variant: "destructive",
      });
    });

    // Check initial connection status
    fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/status`)
      .then(res => res.json())
      .then(data => {
        setIsConnected(data.isConnected);
        if (data.isConnected) {
          setWhatsappName(data.whatsappName || "");
        }
      })
      .catch(error => {
        console.error('Error checking status:', error);
      });

    return () => {
      socket.disconnect();
    };
  }, [toast]);

  const handleInitialize = async () => {
    try {
      setIsInitializing(true);
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/initialize`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Initialization error:', error);
      toast({
        title: "Erro na Inicialização",
        description: "Não foi possível iniciar a conexão com o WhatsApp.",
        variant: "destructive",
      });
      setIsInitializing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/disconnect`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Disconnect error:', error);
      toast({
        title: "Erro ao Desconectar",
        description: "Não foi possível desconectar do WhatsApp.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8 p-6">
      <div>
        <h1 className="text-3xl font-heading font-bold text-gray-900">Conexão WhatsApp</h1>
        <p className="mt-2 text-gray-600">Conecte sua conta do WhatsApp para enviar mensagens</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Status da Conexão</CardTitle>
          <CardDescription>
            {isConnected ? (
              <>
                <span className="text-green-500 font-medium">Conectado</span>
                {whatsappName && ` - ${whatsappName}`}
              </>
            ) : (
              <span className="text-red-500 font-medium">Desconectado</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-end">
            {isConnected ? (
              <Button onClick={handleDisconnect} variant="destructive">
                Desconectar
              </Button>
            ) : (
              <Button 
                onClick={handleInitialize} 
                disabled={isInitializing}
              >
                {isInitializing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Iniciando...
                  </>
                ) : (
                  "Conectar WhatsApp"
                )}
              </Button>
            )}
          </div>

          {qrCode && (
            <div className="flex flex-col items-center space-y-4 p-6 border rounded-lg bg-white">
              <h3 className="text-lg font-medium">Escaneie o Código QR</h3>
              <p className="text-sm text-gray-500 text-center max-w-md">
                Abra o WhatsApp no seu celular, vá em Configurações {'>'} WhatsApp Web/Desktop e escaneie o código QR abaixo
              </p>
              <img 
                src={`data:image/png;base64,${qrCode}`}
                alt="WhatsApp QR Code" 
                className="w-64 h-64"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConnectionPage;